import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useVisualMode } from '../../utils/visualMode';
import { FaStar, FaRegStar } from 'react-icons/fa';
import { Clock3 } from 'lucide-react';
import ResponsiveButton from '../ResponsiveButton/NeonButton.jsx';
import { absolutize, LOCAL_BAND_PLACEHOLDER } from '../../utils/media.js';

/* ---------------- helpers ---------------- */
const getCardProfileSrc = (band = {}) => {
  const src =
    band.profile_image_card ||
    band.profile_image_override ||
    band.profile_image_url ||
    band.profile_image ||
    band.placeholder_image_url ||
    band.placeholder_image ||
    LOCAL_BAND_PLACEHOLDER;

  return src || LOCAL_BAND_PLACEHOLDER;
};

const getYouTubeId = (url = '') => {
  const s = String(url).trim();
  if (!s) return null;
  let m = s.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  m = s.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  m = s.match(/\/shorts\/([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  m = s.match(/embed\/([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};

const prettyType = (raw) => {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  if (s.includes('standup') || s.includes('stand up')) return 'Stand up -koomikko';
  if (/(trub|truba)/.test(s)) return 'Trubaduuri';
  if (/duo/.test(s)) return 'Duo'; // 👈 kaikki duot → "Duo"
  // ✅ Erottele bilebändi vs. "tavallinen bändi" (esim. "Bändi (oma musiikki)")
  // - "bile" -> Bilebändi
  // - muut bändit -> Bändi
  if (/bile/.test(s)) return 'Bilebändi';
  if (/(bändi|band)/.test(s)) return 'Bändi';
  if (/dj/.test(s)) return 'DJ';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Ohjelmien fallback-labelit (slug → näkyvä nimi)
const PROGRAM_FALLBACK_LABELS = {
  "magic-stage": "Lavashow",
  "magic-closeup": "Lähitaikuus / kiertävä pöytätaikuus",
  "magic-mentalism": "Mentalismi",
  "magic-custom": "Räätälöity show (yritykset / teemat)",
  "magic-kids": "Lasten taikashow",
};

/**
 * Palauttaa true jos teksti näyttää "slugilta" (esim. magic-kids / magic_kids / lasten-juhlat)
 * - vaatii vähintään yhden erotinmerkin (- tai _), jotta esim. "Lavashow" ei tulkitu slugiksi
 */
const isProbablySlug = (s) => {
  const t = String(s || '').trim();
  if (!t) return false;
  // sallitaan a-z0-9 sekä välissä - tai _
  return /^[a-z0-9]+([_-][a-z0-9]+)+$/i.test(t);
};

/**
 * Ohjelma-token -> ihmisen luettava label
 * - jos löytyy fallback-mapista (slug -> label), käytä sitä
 * - jos näyttää slugilta mutta ei löydy mapista, prettify slugista (foo-bar -> Foo bar)
 * - jos EI näytä slugilta, palauta alkuperäinen (säilyttää isot kirjaimet, slashit, jne.)
 */
const mapProgramToken = (token) => {
  const original = String(token || '').trim();
  if (!original) return '';

  const key = original.toLowerCase().trim();
  const normKey = key.replace(/_/g, '-'); // varmuus: magic_kids -> magic-kids

  if (PROGRAM_FALLBACK_LABELS[normKey]) return PROGRAM_FALLBACK_LABELS[normKey];
  if (isProbablySlug(original)) return prettyFromSlug(original);
  return original;
};

// Juontotyylit: jos tulee slugina, siisti muotoon. Muuten palauta sellaisenaan.
const mapHostStyleToken = (token) => {
  const original = String(token || '').trim();
  if (!original) return '';
  if (isProbablySlug(original)) return prettyFromSlug(original);
  return original;
};

/* ---------- Event-tyyppien fallback-labelit (jos backend ei palauta valmiita nimiä) ---------- */
const EVENT_TYPE_FALLBACK_LABELS = {
  ravintola: 'Ravintola / baari',
  festarit: 'Festivaalit',
  yksityiskeikka: 'Yksityiskeikka',
  haat: 'Häät',
  yritystilaisuus: 'Yritystilaisuus',
  syntymapaiva: 'Syntymäpäivä',
  syntymapaivät: 'Syntymäpäivä',
};

// "lasten-juhlat" -> "Lasten juhlat" (ja fallback-map jos löytyy)
const prettyFromSlug = (slug) => {
  const s = String(slug || '').trim();
  if (!s) return '';

  // ✅ jos on pelkkä numero, palauta se sellaisenaan (älä riko ID-arvoja)
  if (/^\d+$/.test(s)) {
    return s;
  }

  const key = s.toLowerCase();
  if (EVENT_TYPE_FALLBACK_LABELS[key]) return EVENT_TYPE_FALLBACK_LABELS[key];
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
};

const firstYouTubeIdFromMultiline = (raw = '') =>
  String(raw)
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map(getYouTubeId)
    .find(Boolean) || null;

    // Pakota vähintään pieni pystysuuntainen "rajausvara", jotta object-position (Y) vaikuttaa
    // myös leveissä/vaakakuvissa (16:9-kortissa muuten ei aina synny pystyleikkausta).
    const MIN_VERTICAL_OVERFLOW_PCT = 0.18; // 18% kortin korkeudesta


 /**
 * image_focus_y -> object-position (Y)
 * Tukee sekä:
 * - 0..100   = prosentti
 * - 0..1     = suhdeluku (0.0..1.0)
 * - -10..10  = "offset" keskeltä (0 => 50%, 3 => 65%, -3 => 35%)
 * Palauttaa null jos arvoa ei voi tulkita.
 */
const focusYToObjectPosition = (focusY) => {
  const n = Number(focusY);
  if (!Number.isFinite(n)) return null;

  // (valinnainen) tuki desimaaliselle 0..1 muodolle (esim. 0.23 = 23%)
  // Huom: kokonaisluvut 0 ja 1 tulkitaan prosenteiksi (0% ja 1%), ei ratioiksi.
  if (n > 0 && n < 1 && !Number.isInteger(n)) {
    const pct = Math.max(0, Math.min(100, Math.round(n * 100)));
    return `50% ${pct}%`;
  }

  // 0..100 -> prosentti sellaisenaan
  if (n >= 0 && n <= 100) {
    const pct = Math.max(0, Math.min(100, n));
    return `50% ${pct}%`;
  }

  return null;
};

/** ---------------------------------------------------------------
 * ydin-apuri: array | object[] | string(,,-/JSON/rivinvaihdot) -> string[]
 * - object[]: käyttää name/region/title/label -kenttiä
 * - string: ymmärtää JSON-arrayn, rivinvaihdot ja pilkut
 * --------------------------------------------------------------*/
const toStringArray = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) {
    return val
      .map((x) =>
        typeof x === 'object' && x
          ? (x.label ?? x.name ?? x.region ?? x.title ?? '')
          : String(x)
      )
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const s0 = String(val).trim();
  if (!s0) return [];
  // jos JSON-taulukko
  if (s0.startsWith('[') && s0.endsWith(']')) {
    try {
      const j = JSON.parse(s0);
      if (Array.isArray(j)) return toStringArray(j);
    } catch {}
  }
  // rivinvaihdot/pilkut
  return s0.replace(/\n+/g, ',').split(',').map((s) => s.trim()).filter(Boolean);
};

/**
 * Rajaa listan korttiin:
 * - näyttää max kpl
 * - palauttaa myös kuinka monta jäi piiloon (more)
 */
const summarizeList = (items, max = 4) => {
  const arr = (items || []).map((s) => String(s || "").trim()).filter(Boolean);
  if (!arr.length) return { text: "", more: 0, all: [] };
  if (arr.length <= max) return { text: arr.join(", "), more: 0, all: arr };
  return { text: arr.slice(0, max).join(", "), more: arr.length - max, all: arr };
};

// ✅ Ota ensimmäinen EI-tyhjä array (vältetään JS-ansa: [] on truthy)
const pickFirstNonEmptyArray = (...arrs) =>
  arrs.find((a) => Array.isArray(a) && a.length) || [];

/* ---------- Non-music tekniikkaheuristiikka (fallback jos backend ei erottele) ---------- */
const TECH_KEYWORDS = [
  'mikrofoni',
  'mikki',
  'headset',
  'kaiutin',
  'äänentoisto',
  'mikseri',
  'monitori',
  'subwoofer',
  'subbari',
  'savukone',
  'valo',
  'valot',
  'dmx',
  'pa ',
  ' pa',
  'p.a',
];

const ALL_FINNISH_REGIONS = [
  'Ahvenanmaa',
  'Etelä-Karjala',
  'Etelä-Pohjanmaa',
  'Etelä-Savo',
  'Kainuu',
  'Kanta-Häme',
  'Keski-Pohjanmaa',
  'Keski-Suomi',
  'Kymenlaakso',
  'Lappi',
  'Pirkanmaa',
  'Pohjanmaa',
  'Pohjois-Karjala',
  'Pohjois-Pohjanmaa',
  'Pohjois-Savo',
  'Päijät-Häme',
  'Satakunta',
  'Uusimaa',
  'Varsinais-Suomi',
];

const normalizeRegionName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/-/g, '-');

const isTechServiceToken = (token) => {
  const t = String(token || '').trim().toLowerCase();
  if (!t) return false;
  return TECH_KEYWORDS.some((kw) => t.includes(kw));
};

/* ---------------- component ---------------- */
const ArtistCard = ({
  band,
  onViewProfile,
  highlight,
  badgeText = 'Kuukauden esiintyjä',
  enableVideo = true,
  showStartingPrice = true,
  imagePriority = false,
}) => {
  const { isMinimal } = useVisualMode();
  const navigate = useNavigate();
  const location = useLocation();
  const SHOW_TYPE_ROW = false;

  // ✅ Korttityypit pitää laskea ENNEN kuin niitä käytetään (programsArr käyttää isMusicCardType)
  const rawType = band.artist_type_name ?? band.artist_type ?? band.type ?? '';
  const typeLabel = prettyType(rawType);
  const typeKey = String(rawType || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/-/g, '-');

  const normalizedTypeKey =
    typeKey.includes('standup') || typeKey.includes('stand-up')
      ? 'standup-koomikko'
      : typeKey;

  const isStandupCardType = normalizedTypeKey === 'standup-koomikko';
  
  // Aikapohjaisessa varausmallissa kortissa voidaan näyttää laskennallinen alk. €/h -hinta.
  // Itse laskenta kannattaa tehdä backendissä maakunnan halvimman hinnan perusteella.
  const bookingMode = String(
    band?.booking_mode ??
    band?.bookingMode ??
    'day'
  ).toLowerCase();
  const isTimedBookingMode = bookingMode === 'timed';

  const isPhotographerCardType =
    normalizedTypeKey.startsWith('valokuvaaja') || normalizedTypeKey.includes('photographer');
  const isHostCardType = normalizedTypeKey.startsWith('juontaja');
  const isNonMusicCardType = [
    'taikuri',
    'standup',
    'standup-koomikko',
    'juontaja',
    'valokuvaaja',
  ].includes(normalizedTypeKey);
  const isMusicCardType = !isNonMusicCardType;

  // Video
  const videoId = useMemo(() => {
    const candidate =
      band.first_video_url ||
      band.video_link ||
      (Array.isArray(band.videos) && band.videos[0]?.url) ||
      '';
    return firstYouTubeIdFromMultiline(candidate);
  }, [band.first_video_url, band.video_link, band.videos]);

  const videoSrc = useMemo(
    () =>
      videoId
        ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1&playsinline=1&rel=0`
        : null,
    [videoId]
  );

  // Näyttöleveys & reduced motion – useState + useEffect (SSR-ystävällinen)
  const [desktop, setDesktop] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);

  const mediaRef = useRef(null);
  const imgMetaRef = useRef({ w: 0, h: 0 }); // naturalWidth/naturalHeight talteen
  const [inView, setInView] = useState(false);
  const [imgSrc, setImgSrc] = useState(getCardProfileSrc(band));
  // Dynaaminen kuvan painopiste (object-position)
  const [objectPos, setObjectPos] = useState(() => {
    const pos = focusYToObjectPosition(band?.image_focus_y);
    return pos || '50% 50%';
  });
  // Pysyvä ratkaisu: skaalaa tarvittaessa hieman lisää, jotta Y-fokus toimii myös vaakakuvissa
  const [imgScale, setImgScale] = useState(1);
  const [hovered, setHovered] = useState(false);
  const [videoMounted, setVideoMounted] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [cooldown, setCooldown] = useState(null);

  // Laske lisä-skaala niin, että pystysuuntaan syntyy minimi "overflow" (-> object-position Y vaikuttaa)
  const recomputeImageScale = useCallback(() => {
    const { w: nw, h: nh } = imgMetaRef.current || {};
    const box = mediaRef.current?.getBoundingClientRect?.();
    const cw = box?.width || 0;
    const ch = box?.height || 0;
    if (!(nw > 0 && nh > 0 && cw > 0 && ch > 0)) return;

    // object-fit: cover -logiikan "perusskaala"
    const coverScale = Math.max(cw / nw, ch / nh);
    const displayedH = nh * coverScale;

    // paljonko pystysuunnassa leikkautuu ilman lisäzoomausta
    const overflow0 = Math.max(0, displayedH - ch);
    const minOverflow = ch * MIN_VERTICAL_OVERFLOW_PCT;

    // jos ei synny tarpeeksi overflow'ta (vaakakuvat), lisää zoomia juuri tarvittava määrä
    const extraScale = overflow0 >= minOverflow
      ? 1
      : (ch + minOverflow) / (displayedH || ch);

    setImgScale(extraScale);
  }, []);

  // Päivitä skaala myös kun kortin koko muuttuu (responsiivisuus)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const el = mediaRef.current;
    if (!el) return;

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => recomputeImageScale());
      ro.observe(el);
      return () => ro.disconnect();
    }

    const onResize = () => recomputeImageScale();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeImageScale]);

  // ennen returnia:
  const resolvedImg = useMemo(() => {
    if (!imgSrc) return LOCAL_BAND_PLACEHOLDER; // tyhjä -> paikallinen placeholder
    if (imgSrc.startsWith('/delva-media/')) return imgSrc; // public-kansion asset, älä absolutisoi
    return absolutize(imgSrc); // palvelimen media -> absolutisoi
  }, [imgSrc]);

  /**
   * ✅ Backendin image_focus_y käyttö heti
   * - asetetaan objectPos välittömästi, jotta ei tule “välähdystä” väärällä rajauksella
   * - toimii myös silloin, kun kuva on cachetettu ja onLoad laukeaa nopeasti/eri järjestyksessä
   */
useEffect(() => {
  const pos = focusYToObjectPosition(band?.image_focus_y);
  if (pos) setObjectPos(pos);
  setTimeout(recomputeImageScale, 0); // pieni viive, antaa positionin ehtiä
}, [band?.image_focus_y, recomputeImageScale]);

  // Päätä kuvan object-position luonnollisen koon perusteella (tai band.image_focus_y)
  const handleImgLoad = (e) => {
    const img = e.currentTarget;
    const w = img.naturalWidth || 0;
    const h = img.naturalHeight || 0;
    imgMetaRef.current = { w, h };
    recomputeImageScale();

    // 1) Jos backend antoi image_focus_y, käytä sitä (varmuuden vuoksi myös onLoadissa)
    const pos = focusYToObjectPosition(band?.image_focus_y);
    if (pos) { setObjectPos(pos); return; }

    // 2) Muuten kevyt heuristiikka kuvan kuvasuhteesta
    if (!w || !h) return;
    const ratio = h / w; // >1 = pysty, <1 = vaaka

    // Pystykuvissa nostetaan fokusta ylemmäs (kasvot usein yläosassa),
    // vaakakuvissa hieman alemmas (usein horisontti/aihe keskellä)
    if (ratio > 1.2) {
      // hieman aggressiivisempi nosto kuin 25%, jotta päät katkeaa harvemmin
      setObjectPos('50% 20%');
    } else if (ratio < 0.8) {
      setObjectPos('50% 45%');
    } else {
      setObjectPos('50% 40%');
    }
  };

  // Päivitä desktop/reducedMotion matchMedia:lla
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mqDesktop = window.matchMedia('(min-width:1024px)');
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    const update = () => {
      setDesktop(mqDesktop.matches);
      setReducedMotion(mqReduce.matches);
    };

    update();

    // addEventListener jos on olemassa, fallback addListeneriin
    if (typeof mqDesktop.addEventListener === 'function') {
      mqDesktop.addEventListener('change', update);
      mqReduce.addEventListener('change', update);
    } else if (typeof mqDesktop.addListener === 'function') {
      mqDesktop.addListener(update);
      mqReduce.addListener(update);
    }

    return () => {
      if (typeof mqDesktop.removeEventListener === 'function') {
        mqDesktop.removeEventListener('change', update);
        mqReduce.removeEventListener('change', update);
      } else if (typeof mqDesktop.removeListener === 'function') {
        mqDesktop.removeListener(update);
        mqReduce.removeListener(update);
      }
    };
  }, []);

  const canPlayVideo = enableVideo && !!videoId && desktop && !reducedMotion;

  useEffect(() => {
    if (!canPlayVideo || !mediaRef.current) return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.25,
    });
    io.observe(mediaRef.current);
    return () => io.disconnect();
  }, [canPlayVideo]);

  useEffect(() => {
    setImgSrc(getCardProfileSrc(band));
  }, [band]); // ok tähän tarkoitukseen

  useEffect(() => {
    if (!(canPlayVideo && inView)) return;
    if (hovered && !videoMounted) {
      const t = setTimeout(() => setVideoMounted(true), 120);
      return () => clearTimeout(t);
    }
  }, [hovered, canPlayVideo, inView, videoMounted]);

  const onMediaEnter = () => {
    setHovered(true);
    if (cooldown) {
      clearTimeout(cooldown);
      setCooldown(null);
    }
  };

  const onMediaLeave = () => {
    setHovered(false);
    const t = setTimeout(() => {
      setVideoMounted(false);
      setVideoReady(false);
    }, 200);
    setCooldown(t);
  };

  useEffect(() => () => cooldown && clearTimeout(cooldown), [cooldown]);

  useEffect(() => {
    setVideoMounted(false);
    setVideoReady(false);
  }, [videoId]);

  const showIframe = canPlayVideo && inView && hovered && videoMounted;

  const currentSearch = location?.search || '';

const areaLabel = isPhotographerCardType ? 'Toiminta-alueet' : 'Esiintymisalueet';

const regionsArr = useMemo(() => {
  const rawRegions = pickFirstNonEmptyArray(
    toStringArray(band?.performance_regions),
    toStringArray(band?.regions),
    toStringArray(band?.performance_regions_string),
    toStringArray(band?.regions_string)
  );

  // Vain valokuvaajalle: jos kaikki maakunnat löytyvät listasta, näytä "Koko Suomi"
  if (isPhotographerCardType && rawRegions.length) {
    const normalizedSet = new Set(rawRegions.map(normalizeRegionName));
    const coversAllFinland = ALL_FINNISH_REGIONS.every((region) =>
      normalizedSet.has(normalizeRegionName(region))
    );

    if (coversAllFinland) return ['Koko Suomi'];
  }

  return rawRegions;
  }, [
  isPhotographerCardType,
  band?.performance_regions,
  band?.regions,
  band?.performance_regions_string,
  band?.regions_string,
]);

const regionsSummary = useMemo(
  () => summarizeList(regionsArr, 4),
  [regionsArr]
);

  // ✅ Tilaisuudet: tehdään array + korttiin tiivistelmä
  const eventTypesArr = useMemo(() => {
    // 1) paras: valmiit labelit
    const fromLabels = toStringArray(band?.event_type_labels);
    if (fromLabels.length) return fromLabels;

    // 2) valmiiksi stringinä
    const fromStr = String(band?.event_types_string || '').trim();
    if (fromStr) return toStringArray(fromStr).map(prettyFromSlug).filter(Boolean);

    // 3) slugit / id:t / lista → poimi ensimmäinen ei-tyhjä ja "prettify"
    const arr = pickFirstNonEmptyArray(
      toStringArray(band?.event_types),
      toStringArray(band?.event_type_slugs),
      toStringArray(band?.event_type_ids),
    );

    return arr
      .map((v) => {
        const s = String(v || '').trim();
        if (!s) return null;
        // jos ID löytyy, mapataan mahdollinen fallback-label jos tunnettu
        if (EVENT_TYPE_FALLBACK_LABELS[s.toLowerCase()]) return EVENT_TYPE_FALLBACK_LABELS[s.toLowerCase()];
        return prettyFromSlug(s);
      })
      .filter(Boolean);
  }, [
    band?.event_type_labels,
    band?.event_types_string,
    band?.event_types,
    band?.event_type_slugs,
    band?.event_type_ids,
  ]);

  const eventTypesSummary = useMemo(
    () => summarizeList(eventTypesArr, 4),
    [eventTypesArr]
  );

  // ✅ Ohjelmat / lisäpalvelut: array + korttiin tiivistelmä
  const programsArr = useMemo(() => {
    if (!isMusicCardType) return [];
    // 1) paras: valmiit labelit backendistä
    const labels = toStringArray(band?.additional_service_labels);
    if (labels.length) return labels.map(mapProgramToken).filter(Boolean);

    // 2) seuraavaksi: ota ensimmäinen ei-tyhjä lähde (array tai string)
    const parts = pickFirstNonEmptyArray(
      toStringArray(band?.additional_services),
      toStringArray(band?.additional_services_string)
    );
    if (!parts.length) return [];

    // 3) mäppää vain slugimaiset tokenit -> label, muuten säilytä teksti sellaisenaan
    return parts.map(mapProgramToken).filter(Boolean);
  }, [
    isMusicCardType,
    band?.additional_service_labels,
    band?.additional_services_string,
    band?.additional_services,
  ]);

  const programsSummary = useMemo(
    () => summarizeList(programsArr, 4),
    [programsArr]
  );

  // ✅ Valokuvaajan palvelut
  const photoServicesArr = useMemo(() => {
    const labels = toStringArray(band?.photo_service_labels);
    if (labels.length) return labels;

    const parts = pickFirstNonEmptyArray(
      toStringArray(band?.photo_services),
      toStringArray(band?.photo_services_string),
      toStringArray(band?.photo_services_string),
      toStringArray(band?.photo_service_ids),
      toStringArray(band?.photoServices),
      toStringArray(band?.photoServicesString),
      toStringArray(band?.photoServiceIds)
    );
      return parts
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .map(prettyFromSlug);
  }, [
    band?.photo_service_labels,
    band?.photo_services,
    band?.photo_services_string,
    band?.photo_service_ids,
    band?.photoServices,
    band?.photoServicesString,
    band?.photoServiceIds,
  ]);

  const photoServicesSummary = useMemo(
    () => summarizeList(photoServicesArr, 4),
    [photoServicesArr]
  );

  const photoAddonArr = useMemo(() => {
    const labels = toStringArray(band?.photo_addon_service_labels);
    if (labels.length) return labels;

    const parts = pickFirstNonEmptyArray(
      toStringArray(band?.photo_addon_services),
      toStringArray(band?.photo_addon_services_string),
      toStringArray(band?.photo_addon_service_ids),
      toStringArray(band?.photoAddonServices),
      toStringArray(band?.photoAddonServicesString),
      toStringArray(band?.photoAddonServiceIds)
      
    );
    return parts
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .map(prettyFromSlug);
  }, [
    band?.photo_addon_service_labels,
    band?.photo_addon_services,
    band?.photo_addon_services_string,
    band?.photo_addon_service_ids,
    band?.photoAddonServices,
    band?.photoAddonServicesString,
    band?.photoAddonServiceIds,
  ]);

  const photoAddonSummary = useMemo(
    () => summarizeList(photoAddonArr, 4),
    [photoAddonArr]
  );

  const photoFacilitiesArr = useMemo(() => {
    const labels = toStringArray(band?.photo_facility_labels);
    if (labels.length) return labels;

    const parts = pickFirstNonEmptyArray(
      toStringArray(band?.photo_facilities),
      toStringArray(band?.photo_facilities_string),
      toStringArray(band?.photo_facility_ids),
      toStringArray(band?.photoFacilities),
      toStringArray(band?.photoFacilitiesString),
      toStringArray(band?.photoFacilityIds)
    );
    return parts
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .map(prettyFromSlug);
  }, [
    band?.photo_facility_labels,
    band?.photo_facilities,
    band?.photo_facilities_string,
    band?.photo_facility_ids,
    band?.photoFacilities,
    band?.photoFacilitiesString,
    band?.photoFacilityIds,
  ]);

  const photoFacilitiesSummary = useMemo(
    () => summarizeList(photoFacilitiesArr, 4),
    [photoFacilitiesArr]
  );

  // ✅ YHTEINEN “lisäpalvelut”-unioni (toimii myös listaus-endpointissa, jos nonmusic_* puuttuu)
  // - tärkeää: EI käytetä ??-ketjua, vaan poimitaan eka EI-tyhjä lähde
  const additionalAllArr = useMemo(() => {
    const arr = pickFirstNonEmptyArray(
      toStringArray(band?.additional_service_labels),
      toStringArray(band?.additional_services),
      toStringArray(band?.additional_services_string)
    );
    return arr.map(mapProgramToken).filter(Boolean);
  }, [
    band?.additional_service_labels,
    band?.additional_services,
    band?.additional_services_string,
  ]);

  // ✅ Fallback-splitti additionalAllArr:sta (tekniikka vs ei-tekniikka)
  // - näitä käytetään, jos backend ei palauta nonmusic_addon_* / nonmusic_tech_* kenttiä listauksessa
  const fallbackPrimaryFromAdditionalArr = useMemo(
    () => additionalAllArr.filter((x) => !isTechServiceToken(x)),
    [additionalAllArr]
  );
  const fallbackTechFromAdditionalArr = useMemo(
    () => additionalAllArr.filter((x) => isTechServiceToken(x)),
    [additionalAllArr]
  );
  const fallbackPrimaryFromAdditionalSummary = useMemo(
    () => summarizeList(fallbackPrimaryFromAdditionalArr, 4),
    [fallbackPrimaryFromAdditionalArr]
  );
  const fallbackTechFromAdditionalSummary = useMemo(
    () => summarizeList(fallbackTechFromAdditionalArr, 4),
    [fallbackTechFromAdditionalArr]
  );

  // ✅ Non-music: TYYLIT (nonmusic_style_services)
  // - Standup: esim. "Roast / henkilökohtainen setti", "Improv / vuorovaikutteinen setti"
  // - Taikuri: esim. "Lavashow", "Lähitaikuus"
  //
  // HUOM: jos listaus-endpoint ei vielä palauta nonmusic_style_* kenttiä,
  // fallbackataan additional_services -unionista ja suodatetaan tekniikka pois.
  const nonmusicStyleArr = useMemo(() => {
    // 1) paras: valmiit labelit backendistä
    const labels = toStringArray(band?.nonmusic_style_service_labels);
    if (labels.length) return labels.map(mapProgramToken).filter(Boolean);

    // 2) fallback: array/string
    const parts = pickFirstNonEmptyArray(
      toStringArray(band?.nonmusic_style_services),
      toStringArray(band?.nonmusic_style_services_string)
    );
    if (!parts.length) return [];
    return parts.map(mapProgramToken).filter(Boolean);
  }, [
    band?.nonmusic_style_service_labels,
    band?.nonmusic_style_services,
    band?.nonmusic_style_services_string,
  ]);

  // ✅ JUONTAJA: juontopalvelut tulevat (toistaiseksi) additional_services-kentästä,
  // mutta erotellaan tekniikka omaksi riviksi fallbackina.
  const hostAddonsArr = useMemo(() => {
    // ✅ älä käytä ??-ketjua ([] blokkaa fallbackin)
    // käytetään samaa “yhtä totuutta” kuin fallbackit
    return additionalAllArr;
  }, [
    additionalAllArr
  ]);

  const hostPrimaryArr = useMemo(
    () => hostAddonsArr.filter((x) => !isTechServiceToken(x)),
    [hostAddonsArr]
  );
  const hostTechArr = useMemo(
    () => hostAddonsArr.filter((x) => isTechServiceToken(x)),
    [hostAddonsArr]
  );

  const hostPrimarySummary = useMemo(
    () => summarizeList(hostPrimaryArr, 4),
    [hostPrimaryArr]
  );
  const hostTechSummary = useMemo(
    () => summarizeList(hostTechArr, 4),
    [hostTechArr]
  );

  // ✅ Non-music: ERITTELE "Ohjelmat / Juontopalvelut" vs "Tekniikka"
  // Backend lähettää nämä erikseen:
  // - nonmusic_addon_*  => ohjelmat / juontopalvelut (esim. "Kuulutus / kuuluttaja", "Juonto / illan läpivienti")
  // - nonmusic_tech_*   => tekniikka (mikrofonit jne.)
  //
  // Kommentti: näitä ei sidota artist-tyyppiin tässä, koska data voi tulla tyhjänä.
  // Näyttöehdot päätetään myöhemmin isNonMusicCardType/isHostCardType mukaan.
  const nonmusicPrimaryArr = useMemo(() => {
    // 1) paras: valmiit labelit backendistä
    const labels = toStringArray(band?.nonmusic_addon_service_labels);
    if (labels.length) return labels.map(mapProgramToken).filter(Boolean);

    // 2) fallback: array/string
    const parts = pickFirstNonEmptyArray(
      toStringArray(band?.nonmusic_addon_services),
      toStringArray(band?.nonmusic_addon_services_string)
    );
    if (!parts.length) return [];

    return parts.map(mapProgramToken).filter(Boolean);
  }, [
    band?.nonmusic_addon_service_labels,
    band?.nonmusic_addon_services,
    band?.nonmusic_addon_services_string,
  ]);

  const nonmusicPrimarySummary = useMemo(
    () => summarizeList(nonmusicPrimaryArr, 4),
    [nonmusicPrimaryArr]
  );

  const nonmusicTechArr = useMemo(() => {
    // 1) paras: valmiit labelit backendistä
    const labels = toStringArray(band?.nonmusic_tech_service_labels);
    if (labels.length) return labels.map(mapProgramToken).filter(Boolean);

    // 2) fallback: array/string
    const parts = pickFirstNonEmptyArray(
      toStringArray(band?.nonmusic_tech_services),
      toStringArray(band?.nonmusic_tech_services_string)
    );
    if (!parts.length) return [];

    return parts.map(mapProgramToken).filter(Boolean);
  }, [
    band?.nonmusic_tech_service_labels,
    band?.nonmusic_tech_services,
    band?.nonmusic_tech_services_string,
  ]);

  const nonmusicTechSummary = useMemo(
    () => summarizeList(nonmusicTechArr, 4),
    [nonmusicTechArr]
  );

  // ✅ Kielet (näytetään vain juontaja/taikuri/standup -tyypeillä)
  const languagesArr = useMemo(() => {
    // ✅ Ota eka EI-tyhjä (vältetään ansa: [] valitaan ja fallbackit jää käyttämättä)
    const arr = pickFirstNonEmptyArray(
      toStringArray(band?.language_labels),
      toStringArray(band?.languages),
      toStringArray(band?.languages_string),
      toStringArray(band?.language_string),
      toStringArray(band?.language),
      toStringArray(band?.lang) // varmuus
    );
      const cleaned = arr
      .map((s) => String(s || '').trim())
      .filter(Boolean);

    // ✅ Jos kieliä ei tule listaus-endpointista, näytä silti jotain.
    // Tämä EI estä "Somaliaa" tms: jos kieli on tallennettu ja tulee backendistä,
    // se näkyy cleaned-listassa sellaisenaan.
    if (!cleaned.length && isNonMusicCardType && !isPhotographerCardType) return ['Suomi'];

    return cleaned;
  }, [
    isNonMusicCardType,
    isPhotographerCardType,
    band?.languages,
    band?.language_labels,
    band?.languages_string,
    band?.language_string,
    band?.language,
    band?.lang,
  ]);

  const languagesSummary = useMemo(
    () => summarizeList(languagesArr, 4),
    [languagesArr]
  );

  // ✅ Juontotyylit (näytetään vain Juontajalla)
  const hostStylesArr = useMemo(() => {
  // 1️⃣ Jos backend antaa valmiit labelit → käytä sellaisenaan (älä muokkaa)
  const labels = toStringArray(band?.host_style_labels);
  if (labels.length) return labels;

  // 2️⃣ Muuten fallback slug → prettify
  const arr = pickFirstNonEmptyArray(
    toStringArray(band?.host_styles),
    toStringArray(band?.hostStyles),
    toStringArray(band?.host_style),
    toStringArray(band?.host_styles_string),
    toStringArray(band?.host_style_string)
  );

  return arr.map(mapHostStyleToken).filter(Boolean);
  }, [
    band?.host_style_labels,
    band?.host_styles,
    band?.hostStyles,
    band?.host_style,
    band?.host_styles_string,
    band?.host_style_string,
  ]);

  const hostStylesSummary = useMemo(
    () => summarizeList(hostStylesArr, 4),
    [hostStylesArr]
  );

  // Genret tageiksi
  const genresArr = useMemo(
    () => toStringArray(band.genres ?? band.genre ?? band.genre_string),
    [band.genres, band.genre, band.genre_string]
  );

  // ✅ Tyylirivin otsikko (näytetään vain taikuri/standup -korteissa, EI juontajalle)
  const styleHeading = useMemo(() => {
    if (!isNonMusicCardType || isHostCardType) return '';
    if (normalizedTypeKey.startsWith('taikuri')) return 'Taikuus (tyyli)';
    if (isStandupCardType) return 'Ohjelmat';
    return 'Tyyli';
  }, [isNonMusicCardType, isHostCardType, normalizedTypeKey, isStandupCardType]);

  // ✅ Tyylit korttiin:
  // - ensisijaisesti nonmusic_style_services
  // - fallback: additional_services (poista tekniikka-tokenit)
  const styleSummary = useMemo(() => {
    if (!isNonMusicCardType || isHostCardType) return { text: '', more: 0, all: [] };

const arr = nonmusicStyleArr;

    return summarizeList(arr, 4);
  }, [isNonMusicCardType, isHostCardType, nonmusicStyleArr]);

  // Näyttöehdot
  const showLanguages = languagesArr.length > 0;
  const showHostStyles = isHostCardType;

  const programsHeading = useMemo(() => {
    if (isPhotographerCardType) return 'Valokuvauspalvelut';
    if (isHostCardType) return 'Juontopalvelut';
    if (isNonMusicCardType) return 'Ohjelmat';
    return 'Lisäpalvelut';
  }, [isPhotographerCardType, isHostCardType, isNonMusicCardType]);

  // ✅ Kortin "palvelut"-rivi (yksi ainoa totuus):
  // - Juontaja: ensisijaisesti additional_services (hostPrimarySummary),
  //            mutta jos backend joskus alkaa palauttaa nonmusic_addon_* myös hostille, käytä sitä.
  // - Taikuri/standup: nonmusic_addon_*
  // - Muusikot: additional_services (programsSummary)
  const primarySummary = useMemo(() => {
    // ✅ Valokuvaaja
    if (isPhotographerCardType) {
      return photoServicesSummary;
    }
    // ✅ 1) Juontaja ensin (juontaja on myös "nonmusic", joten prioriteetti ratkaisee)
    // - käytä nonmusic_addon_* jos se tulee backendistä
    // - muuten fallback additional_services (hostPrimarySummary)
    if (isHostCardType) {
      if (nonmusicPrimarySummary.text) return nonmusicPrimarySummary;
      return hostPrimarySummary;
    }

    // ✅ 2) Taikuri/standup (nonmusic)
    // - käytä nonmusic_addon_* jos löytyy
    // - muuten fallback additional_services (listaus-endpointin varalle)
    if (isNonMusicCardType) {
      // Stand upin ohjelmat näytetään jo styleSummary-rivillä.
      // Estetään saman sisällön tulostuminen toisena "Ohjelmat"-rivinä.
      // Taikurin ja stand upin ohjelmat näytetään jo styleSummary-rivillä.
      // Estetään saman sisällön tulostuminen toisena Ohjelmat-rivinä.
      if (nonmusicStyleArr.length) {
        return { text: '', more: 0, all: [] };
      }

      if (nonmusicPrimarySummary.text) {
        return nonmusicPrimarySummary;
      }

      return fallbackPrimaryFromAdditionalSummary;
    }

    // ✅ 3) Muusikot
    return programsSummary;
  }, [
    isPhotographerCardType,
    photoServicesSummary,
    isHostCardType,
    isNonMusicCardType,
    nonmusicPrimarySummary,
    nonmusicStyleArr,
    hostPrimarySummary,
    fallbackPrimaryFromAdditionalSummary,
    programsSummary,
  ]);

  // ✅ Tekniikka-rivi:
  // - Juontaja: fallback-splitti additional_services:stä (hostTechSummary),
  //            mutta jos backend palauttaa nonmusic_tech_* niin käytetään sitä.
  // - Taikuri/standup: nonmusic_tech_*
  const techSummary = useMemo(() => {
    if (!isNonMusicCardType) return { text: '', more: 0, all: [] };
    // Juontaja: nonmusic_tech_* jos löytyy, muuten hostTechSummary (split additional_services)
    if (isHostCardType) {
      return nonmusicTechSummary.text ? nonmusicTechSummary : hostTechSummary;
    }
    // Taikuri/standup: nonmusic_tech_* jos löytyy, muuten fallback additional_services (listaus-endpoint)
    if (nonmusicTechSummary.text) return nonmusicTechSummary;
    return fallbackTechFromAdditionalSummary;
  }, [
    isNonMusicCardType,
    isHostCardType,
    hostTechSummary,
    nonmusicTechSummary,
    fallbackTechFromAdditionalSummary,
  ]);

  // ✅ Alk. hinta (valitulle maakunnalle)
  // Backendin starting_price on veroton pohjahinta.
  // Jos profiilille on määritetty ALV-kanta, asiakkaalle näytettävä
  // verollinen hinta lasketaan tästä kortissa
  const startingPrice = useMemo(() => {
    const raw = band?.starting_price ?? null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }, [band?.starting_price]);

  // Laskennallinen tuntihinta on myös veroton pohjahinta.
  const startingHourlyPrice = useMemo(() => {
    const raw =
      band?.starting_hourly_price ??
      band?.starting_price_hourly ??
      band?.starting_price_per_hour ??
      band?.calculated_hourly_price ??
      band?.lowest_hourly_price ??
      null;

    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }, [
    band?.starting_hourly_price,
    band?.starting_price_hourly,
    band?.starting_price_per_hour,
    band?.calculated_hourly_price,
    band?.lowest_hourly_price,
  ]);

  // PostgreSQL NUMERIC voi tulla API:sta merkkijonona, esim. "13.5".
  // NULL tarkoittaa, ettei ALV-käsittelyä ole vielä määritetty.
  const vatRate = useMemo(() => {
    const raw = band?.vat_rate;

    if (raw === null || raw === undefined || raw === '') {
      return null;
    }

    const n = Number(String(raw).replace(',', '.'));
    return [0, 13.5, 25.5].includes(n) ? n : null;
  }, [band?.vat_rate]);

  // Profiili päättää erikseen, näytetäänkö kortissa pakettihinta vai laskennallinen tuntihinta.
  // Pelkkä timed-varausmalli EI saa automaattisesti vaihtaa korttia €/h-näyttöön,
  // koska musiikkiprofiileilla timed voi tarkoittaa vain aikakohtaista kalenteria.
  const pricingDisplayMode = String(band?.pricing_display_mode || "package").toLowerCase();
  const showHourlyPriceOnCard = isTimedBookingMode && pricingDisplayMode === "hourly";

  // Veroton hinta, josta kortin asiakkaalle näytettävä hinta lasketaan.
  const visibleStartingPriceNet =
    showHourlyPriceOnCard && startingHourlyPrice != null
      ? startingHourlyPrice
      : startingPrice;

  // Asiakkaalle näytettävä hinta.
  // - NULL: vanha profiili -> näytä nykyinen hinta muuttamatta sitä
  // - 0: ei arvonlisäveroa -> verollinen = veroton
  // - 13.5 / 25.5: lisää valittu ALV
  const visibleStartingPrice = useMemo(() => {
    if (visibleStartingPriceNet == null) return null;
    if (vatRate == null || vatRate === 0) return visibleStartingPriceNet;

    const gross = visibleStartingPriceNet * (1 + vatRate / 100);

    return Math.round((gross + Number.EPSILON) * 100) / 100;
  }, [visibleStartingPriceNet, vatRate]);

  const visibleStartingPriceSuffix =
    showHourlyPriceOnCard && startingHourlyPrice != null ? ' €/h' : ' €';

    const visibleStartingPriceText =
    visibleStartingPrice != null
      ? visibleStartingPrice.toLocaleString('fi-FI', {
          minimumFractionDigits: Number.isInteger(visibleStartingPrice) ? 0 : 2,
          maximumFractionDigits: 2,
        })
      : '';

  const visibleStartingPriceLabel =
    visibleStartingPrice != null
      ? `Alk. ${visibleStartingPriceText}${visibleStartingPriceSuffix}`
      : '';

  // ALV-selite näytetään vain, kun profiili on määrittänyt ALV-käsittelyn.
  // Verottomuutta ei nimetä "ALV 0 %" -verokannaksi.
  const visibleStartingPriceVatLabel =
    vatRate == null
      ? ''
      : vatRate === 0
        ? 'Ei arvonlisäveroa'
        : `sis. ALV ${vatRate.toLocaleString('fi-FI', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          })} %`;

  const visibleStartingPriceAriaLabel =
    visibleStartingPrice == null
      ? ''
      : [
          showHourlyPriceOnCard && startingHourlyPrice != null
            ? `Laskennallinen alkava tuntihinta: ${visibleStartingPriceText} euroa tunnilta`
            : `Alkava hinta: ${visibleStartingPriceText} euroa`,
          vatRate == null
            ? ''
            : vatRate === 0
              ? 'ei arvonlisäveroa'
              : `sisältää arvonlisäveron ${vatRate.toLocaleString('fi-FI', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                })} prosenttia`,
        ]
          .filter(Boolean)
          .join(', ');


  // Kalenterin saatavuustila kortille.
  //
  // TÄRKEÄ:
  // - eksplisiittinen 0 = kalenteriin ei ole avattu vapaita päiviä seuraavalle 90 päivälle
  // - puuttuva/null-arvo = tietoa ei ole, joten sitä EI tulkita tyhjäksi kalenteriksi
  const rawOpenDays90 = band?.open_days_90;

  const hasExplicitOpenDays90 =
    rawOpenDays90 !== null &&
    rawOpenDays90 !== undefined &&
    String(rawOpenDays90).trim() !== '';

  const openDays90 = hasExplicitOpenDays90
    ? Number(rawOpenDays90)
    : null;

  const hasNoOpenDays =
    hasExplicitOpenDays90 &&
    Number.isFinite(openDays90) &&
    openDays90 === 0;

  // koko kortti vie profiiliin
  // Säilytetään haun konteksti mukana URLissa:
  // esim. ?date=2026-05-02&type=valokuvaaja&maakunta=uusimaa
  // Näin profiilisivu voi näyttää saman päivän ja aluekohtaisen Alk.-hinnan.
  // HUOM: kortin perusklikkaus ei saa viedä saatavuuteen.
  // Siksi poistetaan mahdolliset scroll/book-parametrit.
  // ja näyttää saman aluekohtaisen Alk.-hinnan.
  const goToProfile = () => {
    if (!band?.slug) return;

    const params = new URLSearchParams(currentSearch || "");

    // Kortin perusklikkaus = profiilin alkuun.
    // Nämä voivat jäädä URLiin aiemmista pikahaku-/saatavuuspoluista
    // ja aiheuttaa mobiilissa hypyn suoraan saatavuuteen.
    params.delete("date");
    params.delete("scrollTo");
    params.delete("book");
    params.delete("availability");
    params.delete("openCalendar");
    params.delete("calendar");

    const cleanSearch = params.toString() ? `?${params.toString()}` : "";

    // Kortin perusklikkaus vie aina profiilin yläosaan.
    // Ei lisätä hashia, ettei profiilisivu tulkitse klikkausta saatavuus-/lomakehyppyksi.
    onViewProfile?.(`${band.slug}${cleanSearch}`);

    // Varmistus: jos profiilisivu tai selain yrittää palauttaa aiemman scrollin,
    // pakotetaan perusklikkaus takaisin sivun alkuun.
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      }, 150);
    });
  };

  // Tarkista saatavuus → vie suoraan profiilin saatavuuskohtaan.
  // Huom: EI scrollata tässä ylös, ettei ankkuri/hash-scrolli mene rikki.
  const goToContactForm = () => {
    if (!band?.slug) return;

    navigate(`/profiili/${band.slug}${currentSearch}#availability`, {
      state: { scrollTo: "booking" },
    });
  };

  const onKeyActivate = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToProfile();
    }
  };

  // Arviot (puolikkaat tähdet)
  const { avg, full, half, empty, reviewsCount, hasReviews } = useMemo(() => {
    const r = Number(band.average_rating ?? band.rating?.average);
    const a = Number.isFinite(r) ? Math.min(5, Math.max(0, r)) : 0;
    const c0 = Number(band.reviews_count ?? band.rating?.total);
    const c = Number.isFinite(c0) ? Math.max(0, Math.floor(c0)) : 0;

    const full = Math.floor(a);
    const half = a - full >= 0.5 ? 1 : 0;
    const empty = 5 - full - half;

    return { avg: a, full, half, empty, reviewsCount: c, hasReviews: c > 0 };
  }, [band.average_rating, band.reviews_count, band.rating]);

  return (
    <div
      className={[
        // Premium-kortti: selkeä pinta, hillitty syvyys ja sama rakenne kaikissa kokoluokissa.
        "group relative flex h-full w-full min-w-0 max-w-full cursor-pointer flex-col overflow-hidden rounded-[1.9rem]",
        "border border-black/[0.09] bg-white/70 backdrop-blur-xl",
        "shadow-[0_18px_46px_rgba(0,0,0,0.11)]",
        "transition-[transform,box-shadow,border-color,background-color] duration-300 ease-out",
        "dark:border-white/[0.10] dark:bg-night-800/[0.72]",
        "dark:shadow-[0_20px_56px_rgba(0,0,0,0.34)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
        isMinimal
          ? ""
          : "hover:-translate-y-1.5 hover:border-black/[0.14] hover:bg-white/80 hover:shadow-[0_26px_60px_rgba(0,0,0,0.15)] dark:hover:border-white/[0.16] dark:hover:bg-night-800/[0.82] dark:hover:shadow-neon-primary",
      ].join(" ")}
      role="button"
      aria-label={`Avaa profiili: ${band?.name || ''}`}
      tabIndex={0}
      onClick={goToProfile}
      onKeyDown={onKeyActivate}
    >
      {/* Hillitty sisärengas sitoo kuvan, sisällön ja CTA:n yhdeksi premium-kortiksi. */}
      <span className="pointer-events-none absolute inset-0 z-20 rounded-[1.9rem] ring-1 ring-inset ring-white/35 dark:ring-white/[0.06]" />

      {/* Media */}
      <div
        ref={mediaRef}
        className="relative aspect-[16/9] w-full min-w-0 max-w-full overflow-hidden bg-black/[0.06] dark:bg-black/25"
        onMouseEnter={onMediaEnter}
        onMouseLeave={onMediaLeave}
      >
        <img
          src={resolvedImg}
          alt={band?.name || 'Esiintyjä'}
          loading={imagePriority ? 'eager' : 'lazy'}
          fetchPriority={imagePriority ? 'high' : 'auto'}
          decoding={imagePriority ? 'sync' : 'async'}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out"
          style={{
            objectPosition: objectPos,
            transformOrigin: objectPos,
            transform: `scale(${imgScale * (isMinimal ? 1 : (hovered ? 1.025 : 1))})`,
          }}
          onLoad={handleImgLoad}
          onError={() => setImgSrc('')}
        />

        {showIframe && videoSrc && (
          <iframe
            className={`absolute inset-0 h-full w-full object-cover pointer-events-none transition-opacity duration-500 ${
              videoReady ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setVideoReady(true)}
            src={videoSrc}
            title={`${band.name} taustavideo`}
            frameBorder="0"
            loading="lazy"
            allow="autoplay; encrypted-media; picture-in-picture"
            aria-hidden="true"
          />
        )}

        {/* Kuvan alareunaan hieman vahvempi syvyys, jotta kortin vaihto sisältöön tuntuu viimeistellyltä. */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/[0.04] to-black/[0.08]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/25 to-transparent" />

        {/* Kuukauden esiintyjä / muu korostus */}
        {highlight && (
          <div className="absolute left-3 top-3 z-10 rounded-full bg-gradient-to-r from-pink to-secondary px-3 py-1.5 text-[12px] font-semibold tracking-wide text-white shadow-neon-pink">
            <span className="inline-flex items-center gap-1.5">
              <FaStar className="drop-shadow-sm" aria-hidden="true" />
              {badgeText}
            </span>
          </div>
        )}

        {/* Tyyppi */}
        {typeLabel && (
          <span
            className="
              absolute right-3 top-3 z-10 rounded-full border border-white/20
              bg-black/60 px-3 py-1.5 text-[11px] font-semibold tracking-wide
              text-white shadow-lg backdrop-blur-md
            "
            aria-label={`Tyyppi: ${typeLabel}`}
            title={`Tyyppi: ${typeLabel}`}
          >
            {typeLabel}
          </span>
        )}
      </div>

      {/* Header: nimi ja sijainti saavat koko leveyden. Hinta on oma responsiivinen premium-rivinsä. */}
      <div className="min-w-0 px-4 pb-4 pt-4 sm:px-5 sm:pb-5 sm:pt-5">
        <div className="min-w-0">
          <h3 className="min-w-0 whitespace-normal break-normal font-customFont2 text-left text-[1.5rem] font-semibold leading-[1.04] tracking-tight text-black/90 [overflow-wrap:normal] [word-break:normal] [hyphens:none] dark:text-primary sm:text-[1.65rem]">
            {band.name}
          </h3>

          {band.location && (
            <p className="mt-2 font-customFont1 text-sm font-medium text-black/50 dark:text-white/50">
              {band.location}
            </p>
          )}
        </div>

        {showStartingPrice && visibleStartingPrice != null && (
          <div
            className="
              mt-4 flex min-w-0 items-center justify-between gap-3 overflow-hidden
              rounded-[1.2rem] border border-black/[0.08]
              bg-gradient-to-r from-black/[0.045] via-black/[0.018] to-transparent
              px-3.5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]
              dark:border-white/[0.09]
              dark:from-primary/[0.10] dark:via-white/[0.045] dark:to-transparent
              dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
            "
            aria-label={visibleStartingPriceAriaLabel}
            title={[visibleStartingPriceLabel, visibleStartingPriceVatLabel]
              .filter(Boolean)
              .join(' · ')}
          >
            <div className="min-w-0">
              <div className="font-customFont1 text-[10px] font-bold uppercase tracking-[0.16em] text-black/45 dark:text-white/45">
                <span>Alkaen</span>
              </div>

              {visibleStartingPriceVatLabel && (
                <div className="mt-0.5 truncate font-customFont1 text-[9px] font-medium leading-tight text-black/40 dark:text-white/40">
                  <span>{visibleStartingPriceVatLabel}</span>
                </div>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              <span className="font-customFont2 text-[1.35rem] font-semibold leading-none tracking-tight text-black/90 dark:text-primary sm:text-[1.5rem]">
                {visibleStartingPriceText}
              </span>

              <span className="font-customFont1 text-xs font-bold text-black/55 dark:text-white/60">
                <span>
                  {showHourlyPriceOnCard && startingHourlyPrice != null ? '€/h' : '€'}
                </span>
              </span>

              {showHourlyPriceOnCard && startingHourlyPrice != null && (
                <span
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-black/[0.08] bg-white/55 text-black/55 shadow-sm dark:border-white/[0.10] dark:bg-white/[0.06] dark:text-white/60"
                  aria-hidden="true"
                >
                  <Clock3 size={12} strokeWidth={2.3} />
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sisältö */}
      <div className="flex min-w-0 flex-grow flex-col px-4 pb-4 sm:px-5 sm:pb-5">
        <div
          className="
            min-w-0 max-w-full overflow-hidden rounded-[1.25rem]
            border border-black/[0.06]
            bg-gradient-to-b from-white/55 to-black/[0.018]
            px-3.5 py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]
            dark:border-white/[0.06] dark:from-white/[0.035] dark:to-black/[0.02]
            dark:shadow-none
          "
        >
          <div className="space-y-3">
            {/* Kortissa näytetään vain tiivistelmä: max 2 arvoa / kategoria. */}

            {genresArr.length > 0 && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  Musiikkityylit
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {genresArr.slice(0, 2).join(", ")}
                    {genresArr.length > 2 && (
                      <span className="opacity-75"> +{genresArr.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {regionsSummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  {areaLabel}
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {regionsSummary.all.slice(0, 2).join(", ")}
                    {regionsSummary.all.length > 2 && (
                      <span className="opacity-75"> +{regionsSummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {eventTypesSummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  Tilaisuudet
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {eventTypesSummary.all.slice(0, 2).join(", ")}
                    {eventTypesSummary.all.length > 2 && (
                      <span className="opacity-75"> +{eventTypesSummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {isNonMusicCardType && !isHostCardType && styleSummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  {styleHeading}
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {styleSummary.all.slice(0, 2).join(", ")}
                    {styleSummary.all.length > 2 && (
                      <span className="opacity-75"> +{styleSummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {primarySummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  {programsHeading}
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {primarySummary.all.slice(0, 2).join(", ")}
                    {primarySummary.all.length > 2 && (
                      <span className="opacity-75"> +{primarySummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {isPhotographerCardType && photoAddonSummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  Lisäpalvelut
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {photoAddonSummary.all.slice(0, 2).join(", ")}
                    {photoAddonSummary.all.length > 2 && (
                      <span className="opacity-75"> +{photoAddonSummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {isPhotographerCardType && photoFacilitiesSummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  Studio / puitteet
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {photoFacilitiesSummary.all.slice(0, 2).join(", ")}
                    {photoFacilitiesSummary.all.length > 2 && (
                      <span className="opacity-75"> +{photoFacilitiesSummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {isNonMusicCardType && techSummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  Tekniikka
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {techSummary.all.slice(0, 2).join(", ")}
                    {techSummary.all.length > 2 && (
                      <span className="opacity-75"> +{techSummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {showLanguages && languagesSummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  Kielet
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {languagesSummary.all.slice(0, 2).join(", ")}
                    {languagesSummary.all.length > 2 && (
                      <span className="opacity-75"> +{languagesSummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {showHostStyles && hostStylesSummary.text && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  Juontotyylit
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  <span>
                    {hostStylesSummary.all.slice(0, 2).join(", ")}
                    {hostStylesSummary.all.length > 2 && (
                      <span className="opacity-75"> +{hostStylesSummary.all.length - 2} lisää</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {SHOW_TYPE_ROW && typeLabel && (
              <div className="min-w-0">
                <div className="font-customFont1 text-sm font-extrabold text-black/85 dark:text-electric">
                  Esiintyjätyyppi
                </div>
                <div className="mt-1 font-customFont1 text-sm leading-5 text-black/65 dark:text-white/65">
                  {typeLabel}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Arvostelut */}
        {hasReviews && (
          <div
            className="
              mt-4
              flex
              min-w-0
              items-center
              gap-2.5

              rounded-[1rem]

              border
              border-black/[0.06]

              bg-white/45

              px-3
              py-2.5

              shadow-sm

              dark:border-white/[0.06]
              dark:bg-white/[0.03]
              dark:shadow-none
            "
            aria-label={`Arvosana ${avg.toFixed(1)} / 5, ${
              reviewsCount === 1
                ? "1 arvostelu"
                : `${reviewsCount} arvostelua`
            }`}
            role="img"
          >
            <span
              className="
                shrink-0

                font-customFont1
                text-sm
                font-extrabold

                text-black/80
                dark:text-primary
              "
            >
              {avg.toFixed(1)}
            </span>

            <div className="flex shrink-0 items-center gap-[2px] text-[13px]">
              {Array.from({ length: full }).map((_, i) => (
                <FaStar
                  key={`f${i}`}
                  className="text-yellow-400 drop-shadow-[0_0_2px_rgba(0,0,0,0.35)]"
                  aria-hidden="true"
                />
              ))}

              {half ? (
                <span
                  key="half"
                  className="relative inline-block"
                  style={{
                    width: "1em",
                    height: "1em",
                  }}
                  aria-hidden="true"
                >
                  <FaRegStar className="absolute inset-0 text-gray-400" />

                  <FaStar
                    className="absolute inset-0 text-yellow-400"
                    style={{
                      clipPath: "inset(0 50% 0 0)",
                    }}
                  />
                </span>
              ) : null}

              {Array.from({ length: empty }).map((_, i) => (
                <FaRegStar
                  key={`e${i}`}
                  className="text-gray-400"
                  aria-hidden="true"
                />
              ))}
            </div>

            <span
              className="
                ml-auto
                shrink-0
                whitespace-nowrap

                font-customFont1
                text-[10px]
                font-semibold

                text-black/45
                dark:text-white/45
              "
            >
              {reviewsCount === 1
                ? "1 arvostelu"
                : `${reviewsCount} arvostelua`}
            </span>
          </div>
        )}

        {/* CTA-footer */}
        <div className="mt-auto min-w-0 max-w-full pt-5">
          <div className="mb-3 h-px bg-black/[0.065] dark:bg-white/[0.07]" />

          {hasNoOpenDays && (
            <div
              className="
                mb-3
                rounded-[1rem]
                border border-black/[0.07]
                bg-black/[0.025]
                px-3.5 py-3
                text-center
                dark:border-white/[0.07]
                dark:bg-white/[0.025]
              "
            >
              <div
                className="
                  font-customFont1
                  text-sm
                  font-bold
                  text-black/70
                  dark:text-white/75
                "
              >
                Ei vapaita päiviä
              </div>

              <div
                className="
                  mt-1
                  font-customFont1
                  text-[11px]
                  font-medium
                  leading-snug
                  text-black/45
                  dark:text-white/45
                "
              >
                Voit silti kysyä saatavuutta Buukkerin kautta.
              </div>
            </div>
          )}

          <ResponsiveButton
            variant="chip"
            className={[
              // Pidetään CTA täysin selkeänä: ei irrallista ikonia, ei uutta väripalettia.
              "!w-full !min-w-0 !max-w-full !rounded-[1rem]",
              "!px-4 !py-3.5",
              "!text-sm !font-bold !normal-case !tracking-normal",
              "!text-black/85 dark:!text-white",
              "border border-black/[0.08] dark:border-white/[0.08]",
              "shadow-[0_8px_20px_rgba(0,0,0,0.08)] dark:shadow-none",
              "transition-[transform,filter,box-shadow,border-color] duration-200 ease-out",
              "hover:-translate-y-0.5 hover:border-black/[0.14] hover:brightness-105",
              "hover:shadow-[0_12px_28px_rgba(0,0,0,0.13)]",
              "dark:hover:border-white/[0.14] dark:hover:shadow-neon-primary",
              "active:translate-y-0 active:scale-[0.99]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric",
              "focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
            ].join(' ')}
            onClick={(e) => {
              e.stopPropagation(); // estä kortin onClick → ei tuplanavigointia
              goToContactForm();
            }}
          >
            {hasNoOpenDays
              ? "Kysy viestillä Buukkerilta"
              : band?.recommendation_availability === "ask"
                ? "Kysy saatavuutta"
                : "Tarkista saatavuus"}
          </ResponsiveButton>
        </div>
      </div>
    </div>
  );
};

ArtistCard.propTypes = {
  band: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    slug: PropTypes.string,
    name: PropTypes.string,
    profile_image_card: PropTypes.string,
    profile_image: PropTypes.string,
    performance_regions: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    performance_regions_string: PropTypes.string,
    regions: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    location: PropTypes.string,
    genres: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    genre: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    genre_string: PropTypes.string,

    sets: PropTypes.array,
    instruments: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    instrument_labels: PropTypes.array,
    instruments_string: PropTypes.string,
    additional_services: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    additional_service_labels: PropTypes.array,
    additional_services_string: PropTypes.string,

    // tilaisuudet (uudet tyypit)
    event_type_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    event_types_string: PropTypes.string,
    event_types: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    event_type_slugs: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    event_type_ids: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    // kielet & juontotyylit (uudet)
    languages: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    host_styles: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    // varanimiä / aliasia (jos backend joskus palauttaa näillä)
    language_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    language: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    lang: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    host_style_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    host_styles_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    hostStyleLabels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    hostStyles: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    host_style: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    // (valinnaiset fallbackit, jos joskus tulet käyttämään)
    languages_string: PropTypes.string,
    host_styles_string: PropTypes.string,
    host_style_string: PropTypes.string,
    // fallback jos juontotyylit on joskus nonmusic_style_services-kentissä
    nonmusic_style_services: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    nonmusic_style_service_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    nonmusic_style_services_string: PropTypes.string,

    // ✅ non-music: ohjelmat/juontopalvelut + tekniikka (backendin palauttamat alias-kentät)
    nonmusic_addon_services: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    nonmusic_addon_service_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    nonmusic_addon_services_string: PropTypes.string,

    nonmusic_tech_services: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    nonmusic_tech_service_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    nonmusic_tech_services_string: PropTypes.string,

    // ✅ valokuvaaja
    photo_service_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photo_services: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photo_services_string: PropTypes.string,
    photo_service_ids: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photoServices: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photoServicesString: PropTypes.string,
    photoServiceIds: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

    photo_addon_service_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photo_addon_services: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photo_addon_services_string: PropTypes.string,
    photo_addon_service_ids: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photoAddonServices: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photoAddonServicesString: PropTypes.string,
    photoAddonServiceIds: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

    photo_facility_labels: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photo_facilities: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photo_facilities_string: PropTypes.string,
    photo_facility_ids: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photoFacilities: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    photoFacilitiesString: PropTypes.string,
    photoFacilityIds: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

    open_days_90: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    reviews_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    average_rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    video_link: PropTypes.string,
    first_video_url: PropTypes.string,
    videos: PropTypes.array,
    artist_type: PropTypes.string,
    starting_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    starting_hourly_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    starting_price_hourly: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    starting_price_per_hour: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    calculated_hourly_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lowest_hourly_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    booking_mode: PropTypes.string,
    bookingMode: PropTypes.string,
    pricing_display_mode: PropTypes.string,
    artist_type_name: PropTypes.string,
    type: PropTypes.string,
    // valinnainen manuaalifokus (0–100, y-akseli)
    image_focus_y: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onViewProfile: PropTypes.func,
  highlight: PropTypes.bool,
  badgeText: PropTypes.string,
  enableVideo: PropTypes.bool,
  showStartingPrice: PropTypes.bool,
  imagePriority: PropTypes.bool,
};

export default ArtistCard;